/**
 * Convert single newlines to Markdown hard breaks (two trailing spaces + newline)
 * so that line-by-line content like memo headers doesn't collapse into one paragraph.
 * Skips newlines that are already hard breaks (preceded by two spaces).
 */
export const preserveNewlines = (text) => {
  if (!text) return text;
  return toHardBreaks(repairFlattenedMarkdown(text));
};

const toHardBreaks = (text) => text.replace(/(?<! {2})\n/g, "  \n");

// Lowercase words that legitimately appear inside a Title Case heading
// ("Permission to Record", "Terms of Service"). Any other lowercase word
// following a capitalised word marks the start of body text.
const TITLE_STOPWORDS = new Set([
  "a", "an", "and", "as", "at", "by", "for", "from", "in", "into", "of", "on", "or", "per", "the", "to", "vs", "with",
]);

// Capitalised words that almost never appear mid-heading but very commonly open
// the first sentence of a clause ("Term This Agreement...", "Fees You agree...").
// Matched case-sensitively so all-caps headings ("IT POLICY") are unaffected.
// Deliberately excludes words that do show up in titles: nouns ("Independent
// Contractor Agreement"), and determiners like All / No / Any / Each / Upon
// ("Terms for All Users", "No Warranty", "Upon Termination").
const SENTENCE_OPENERS = new Set([
  "The", "This", "These", "Those", "I", "We", "You", "It", "If", "Except", "Notwithstanding", "Unless", "Nothing",
]);

// "Part I", "Article I", "Exhibit I": a roman-numeral I, not the pronoun.
const NUMERAL_PREFIXES = new Set([
  "Part", "Article", "Exhibit", "Schedule", "Section", "Appendix", "Phase", "Chapter", "Title", "Volume", "Annex",
]);

const isSentenceOpener = (word, prevWord) => {
  const bare = word.replace(/^[("\u201c]+|[)"\u201d,:;]+$/g, "");
  if (bare === "I" && NUMERAL_PREFIXES.has(prevWord)) return false;
  return SENTENCE_OPENERS.has(bare);
};

/**
 * True when `text` looks like Markdown whose newlines were stripped upstream:
 * no line breaks at all, but it starts with a heading marker. Ordinary prose
 * (even prose containing a "#") is never treated as flattened.
 */
export const isFlattenedMarkdown = (text) =>
  typeof text === "string" && !/\n/.test(text) && /^#{1,6}\s\S/.test(text);

/**
 * Split a single flattened heading line ("## 1. Editing Rights Inhouse may edit...")
 * into the heading and the body paragraph that was glued onto it. The heading ends at
 * the first of: an emphasis marker, a numbered-list marker, a table/blockquote marker,
 * a known sentence opener ("This", "The", "I", ...), or a capitalised word followed
 * by a lowercase non-stopword (i.e. the first sentence).
 */
const splitHeadingLine = (line) => {
  const match = line.match(/^(#{1,6}\s+)(.*)$/s);
  if (!match) return line;
  const [, hashes, rest] = match;
  const words = rest.split(" ");
  let cut = words.length;
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    if (/^(\*\*|\*|_|>|\||-{3,}|\d+\.$)/.test(word) || isSentenceOpener(word, words[i - 1])) {
      cut = i;
      break;
    }
    const next = words[i + 1];
    const capitalised = /^[A-Z"“(]/.test(word);
    const nextIsBodyWord =
      next !== undefined && /^[a-z]/.test(next) && !TITLE_STOPWORDS.has(next.replace(/[^a-z]/g, ""));
    if (capitalised && nextIsBodyWord) {
      cut = i;
      break;
    }
  }
  if (cut >= words.length) return line;
  return `${hashes}${words.slice(0, cut).join(" ")}\n\n${words.slice(cut).join(" ")}`;
};

/**
 * Within one non-heading block, re-insert the line structure for lists, tables,
 * blockquotes, horizontal rules and signature-style bold labels.
 */
const repairBlock = (block) => {
  let out = block;

  // Horizontal rules: " --- " / " *** " as a standalone token.
  out = out.replace(/(^|\s)(-{3,}|\*{3,})(?=\s|$)/g, "$1\n\n$2\n\n");

  // Tables: a row boundary in flattened text shows up as "| |". Also start the
  // table on its own line when it follows a sentence ending in ":" or ".".
  out = out.replace(/\|\s+\|/g, "|\n|");
  out = out.replace(/([:.])\s+(\|)/g, "$1\n\n$2");

  // Blockquotes: " > quoted text".
  out = out.replace(/(^|[.:;!?])\s+(>\s)/g, "$1\n\n$2");

  // Numbered lists: "text: 1. First item. 2. Second item." Only split after a
  // sentence boundary so "Section 2. applies" style prose is left alone.
  out = out.replace(/([:.;])\s+(\d{1,3}\.\s)(?=\S)/g, "$1\n\n$2");
  // Consecutive items form a tight list (single newline) rather than a loose one.
  out = out.replace(/\n\n(?=\d{1,3}\.\s)/g, (gap, offset, whole) => {
    const prevLine = whole.slice(whole.lastIndexOf("\n", offset - 1) + 1, offset);
    return /^\d{1,3}\.\s/.test(prevLine) ? "\n" : gap;
  });

  // Bullet lists: "text: - item one - item two". Only treated as a list when a
  // colon introduces the first bullet; every later " - " in the block is then an item.
  if (/:\s+[-•]\s/.test(out)) {
    out = out.replace(/:\s+([-•]\s)/, ":\n\n$1");
    out = out.replace(/(\S)[ \t]+[-•]\s(?=\S)/g, "$1\n- ");
  }

  // Signature blocks / label lines: "___________ **Printed Name:** ___________ **Date:** ___".
  // A bold label that follows a fill-in line or another label starts a new line.
  out = out.replace(/(_{3,}|\*\*)\s+(?=\*\*[^*\n]{1,60}:\*\*)/g, "$1\n\n");
  out = out.replace(/(^|[.:;!?])\s+(?=\*\*[^*\n]{1,60}:\*\*\s+_{3,})/g, "$1\n\n");

  return out;
};

/**
 * Best-effort repair for Markdown that arrived with every newline stripped
 * (INH-4827). Re-inserts blank lines before inline heading markers, separates each
 * heading from the paragraph glued onto it, and restores list / table / rule /
 * blockquote / signature-label line structure. Content that already contains
 * newlines, or that does not start with a heading, is returned untouched.
 *
 * Heading boundaries are inherently ambiguous without newlines, so this is a
 * safety net — the upstream serialiser is the real fix.
 */
export const repairFlattenedMarkdown = (text) => {
  if (!isFlattenedMarkdown(text)) return text;
  return text
    .replace(/\s+(#{1,6}\s)/g, "\n\n$1")
    .split("\n")
    .map((line) => (/^#{1,6}\s/.test(line) ? splitHeadingLine(line) : line))
    .join("\n")
    .split("\n")
    .map((line) => (/^#{1,6}\s/.test(line) ? line : repairBlock(line)))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
};

export const formatText = (text) => {
  if (text) {
    text = text.replace(/\[●\]/g, "[ ]");
    text = text.replace(/`/g, "&#96;");
    text = text.replace(/\\n/g, "  \n");
    text = text.replace(/\n#/g, " \n \n #");
    // Ensure blank line before ordered list items following non-list content.
    // CommonMark only allows "1." to interrupt a paragraph; other numbers
    // (e.g. "3.") are rendered as plain text unless separated by a blank line.
    const textBeforeListFix = text;
    text = text.replace(/\n(\d+\.\s)/g, function (match, listStart, offset) {
      const before = textBeforeListFix.substring(0, offset);
      const prevNewline = before.lastIndexOf("\n");
      const prevLine =
        prevNewline >= 0 ? before.substring(prevNewline + 1) : before;
      if (/^\s*\d+\.\s/.test(prevLine) || prevLine.trim() === "") return match;
      return "\n\n" + listStart;
    });
    text = toHardBreaks(text);
    text = text.replace(/\\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
    text = text.replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
    // Escape square brackets that look like link reference definitions but aren't URLs
    // e.g., [Company Name]: Insert -> \[Company Name\]: Insert
    text = text.replace(/\[([^\]]+)\]:\s*(?!(https?:\/\/|www\.))/g, "\\[$1\\]: ");
  }
  return text;
};
